import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ChevronLeft, ChevronRight, ShieldAlert, ShieldCheck } from "lucide-react";
import { Shell } from "@/components/Shell";
import { InstrumentChecklist } from "@/components/InstrumentChecklist";
import { Masked } from "@/components/Masked";
import { CollapsibleCard } from "@/components/CollapsibleCard";
import { MobileRow } from "@/components/MobileRow";
import { api, errMsg } from "@/lib/api";

const money = (v) => `${(v ?? 0) >= 0 ? "+" : "-"}$${Math.abs(v ?? 0).toFixed(2)}`;
const Label = ({ children }) => (
  <label className="block text-[10px] tracking-[0.14em] uppercase font-semibold text-zinc-500 mb-1.5">
    {children}
  </label>
);
const inputCls =
  "w-full rounded-sm bg-zinc-900/60 border border-zinc-800 px-3 py-2 font-mono text-sm text-zinc-100 focus:border-blue-500 focus:outline-none transition-colors duration-200";
const th = "text-left px-3 py-2 text-[10px] uppercase tracking-[0.12em] font-semibold text-zinc-500 whitespace-nowrap";
const td = "px-3 py-2.5 font-mono text-xs text-zinc-200 whitespace-nowrap";

const Stat = ({ label, value, tone, sub, testid }) => (
  <div className="border border-zinc-800 bg-zinc-900/30 p-4" data-testid={testid}>
    <p className="text-[10px] tracking-[0.14em] uppercase font-semibold text-zinc-500">{label}</p>
    <p className={`font-mono text-xl mt-2 ${tone || "text-zinc-100"}`}>{value}</p>
    {sub && <p className="text-[11px] text-zinc-600 mt-1 font-mono">{sub}</p>}
  </div>
);

export default function TraderDetail() {
  const { userId } = useParams();
  const [detail, setDetail] = useState(null);
  const [mode, setMode] = useState("template");
  const [rules, setRules] = useState(null);
  const [period, setPeriod] = useState("month");
  const [page, setPage] = useState(1);
  const [history, setHistory] = useState(null);
  const [days, setDays] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState([]);

  const loadDetail = useCallback(async () => {
    const { data } = await api.get(`/admin/users/${userId}`);
    setDetail(data);
    setMode(data.has_override ? "custom" : "template");
    setRules({ ...data.effective });
    try {
      const cat = await api.get("/admin/instrument-catalog");
      setCatalog((cat.data.symbols || []).map((s) => s.name));
    } catch {
      setCatalog([]);
    }
  }, [userId]);

  const loadPerf = useCallback(async () => {
    const [h, c, a] = await Promise.all([
      api.get(`/history?user_id=${userId}&period=${period}&page=${page}&per_page=10`),
      api.get(`/calendar?user_id=${userId}&period=${period}`),
      api.get(`/admin/alerts?user_id=${userId}`),
    ]);
    setHistory(h.data);
    setDays(c.data);
    setAlerts(a.data.alerts || []);
  }, [userId, period, page]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    loadPerf();
  }, [loadPerf]);

  const set = (k) => (e) =>
    setRules((r) => ({
      ...r,
      [k]: ["window_start", "window_end", "window_tz", "risk_method", "loss_limit_mode",
            "external_action"].includes(k)
        ? e.target.value
        : Number(e.target.value),
    }));

  const save = async () => {
    setSaving(true);
    try {
      const payload =
        mode === "template"
          ? {}
          : {
              risk_method: rules.risk_method,
              risk_fixed_amount: rules.risk_fixed_amount,
              risk_percent: rules.risk_percent,
              daily_loss_limit: rules.daily_loss_limit,
              loss_limit_mode: rules.loss_limit_mode,
              max_open_trades: rules.max_open_trades,
              max_trades_per_day: rules.max_trades_per_day ?? 0,
              daily_profit_target: rules.daily_profit_target ?? 0,
              window_start: rules.window_start,
              window_end: rules.window_end,
              window_tz: rules.window_tz || "Asia/Kolkata",
              tp1_ratio: rules.tp1_ratio,
              tp1_r: rules.tp1_r,
              tp2_r: rules.tp2_r,
              allowed_instruments: rules.allowed_instruments || [],
              sl_allowance_pct: rules.sl_allowance_pct ?? 25,
              trail_pct: rules.trail_pct ?? 25,
              external_action: rules.external_action || "alert",
              move_be_on_tp1: rules.move_be_on_tp1 !== false,
            };
      await api.put(`/admin/users/${userId}/overrides`, payload);
      toast.success(
        mode === "template"
          ? "Trader is back on the rule template"
          : "Custom rules saved for this trader"
      );
      loadDetail();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleKill = async () => {
    try {
      await api.post(`/admin/users/${userId}/kill-switch`, { active: !detail.kill_switch });
      toast[detail.kill_switch ? "success" : "error"](
        detail.kill_switch ? "Trader can trade again" : "Trader is blocked from trading"
      );
      loadDetail();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!detail || !rules) return <Shell />;

  const p = detail.performance;
  const tpl = detail.template;
  const isCustom = mode === "custom";
  // A trader's list is always a subset of what is globally tradeable.
  const pickable = (tpl.allowed_instruments || []).length
    ? tpl.allowed_instruments
    : catalog;
  const tabCls = (active) =>
    `px-3 py-2 text-xs uppercase tracking-[0.1em] font-semibold rounded-sm border transition-colors duration-200 ${
      active
        ? "border-blue-600 bg-blue-600/15 text-blue-400"
        : "border-zinc-800 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"
    }`;

  return (
    <Shell>
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <Link
              to="/admin"
              data-testid="back-to-admin-link"
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] text-zinc-500 hover:text-zinc-200 transition-colors duration-200 mb-2"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> All traders
            </Link>
            <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight">
              {detail.profile.name ? (
                <Masked value={detail.profile.name} kind="name" testid="detail-trader-name" />
              ) : (
                <Masked value={detail.profile.email} kind="email" testid="detail-trader-name" />
              )}
            </h1>
            <p className="text-xs text-zinc-500 font-mono mt-1" data-testid="trader-meta">
              <Masked value={detail.profile.email} kind="email" testid="trader-meta-email" /> · MT5{" "}
              <span className={detail.mt5_connected ? "text-emerald-400" : "text-red-400"}>
                {detail.mt5_connected ? "live" : "offline"}
              </span>{" "}
              · {detail.profile.mt5_login || "no account"} ·{" "}
              {detail.infra?.pod_name
                ? `${detail.infra.pod_name}:${detail.infra.port}`
                : "no pod assigned"}
              {detail.infra?.latency_ms != null && (
                <span className="text-zinc-600"> · {detail.infra.latency_ms}ms</span>
              )}{" "}
              · Balance $
              {(detail.account.balance ?? 0).toFixed(2)}
              {detail.day_stopped && (
                <span
                  data-testid="detail-day-status"
                  className="ml-2 px-1.5 py-0.5 rounded-sm text-[10px] uppercase font-semibold bg-red-500/10 text-red-400"
                >
                  {detail.day_stop_reason === "auto" ? "day auto-closed · limit hit" : "day closed"}
                </span>
              )}
            </p>
          </div>
          <button
            data-testid="detail-kill-switch-btn"
            onClick={toggleKill}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-sm text-xs font-bold uppercase tracking-[0.12em] border transition-colors duration-200 ${
              detail.kill_switch
                ? "border-emerald-600 text-emerald-400 hover:bg-emerald-600 hover:text-white"
                : "border-red-800 text-red-300 hover:bg-red-600 hover:text-white"
            }`}
          >
            {detail.kill_switch ? (
              <ShieldCheck className="h-4 w-4" />
            ) : (
              <ShieldAlert className="h-4 w-4" />
            )}
            {detail.kill_switch ? "Unblock trader" : "Block trader"}
          </button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="trader-performance">
          <Stat
            label="P&L Today"
            value={money(p.pnl_today)}
            tone={p.pnl_today > 0 ? "text-emerald-400" : p.pnl_today < 0 ? "text-red-400" : ""}
            sub={`Floating ${money(p.floating_pnl)}`}
            testid="stat-today"
          />
          <Stat
            label="P&L This Week"
            value={money(p.pnl_week)}
            tone={p.pnl_week > 0 ? "text-emerald-400" : p.pnl_week < 0 ? "text-red-400" : ""}
            sub={`Month ${money(p.pnl_month)}`}
            testid="stat-week"
          />
          <Stat
            label="Trades Today"
            value={p.trades_today}
            sub={`Open ${p.open_trades} / ${detail.effective.max_open_trades}`}
            testid="stat-trades"
          />
          <Stat
            label="Loss Limit Left"
            value={`$${p.loss_limit_remaining.toFixed(2)}`}
            sub={
              detail.effective.loss_limit_mode === "trailing"
                ? `of $${(p.loss_limit_allowance ?? 0).toFixed(2)} · base $${Number(
                    detail.effective.daily_loss_limit
                  ).toFixed(2)} + booked $${(p.loss_limit_booked ?? 0).toFixed(2)}`
                : `of $${Number(detail.effective.daily_loss_limit).toFixed(2)} · fixed`
            }
            testid="stat-limit"
          />
        </div>

        <CollapsibleCard
          title="Rules"
          storageKey="detail-rules"
          testid="trader-rules-card"
        >
        <div className="p-5 flex flex-col gap-5" data-testid="trader-rules-form">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[11px] text-zinc-600">
                Template default is used unless you switch to custom rules.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                data-testid="rules-mode-template-btn"
                onClick={() => {
                  setMode("template");
                  setRules({ ...tpl });
                }}
                className={tabCls(!isCustom)}
              >
                Use template
              </button>
              <button
                data-testid="rules-mode-custom-btn"
                onClick={() => setMode("custom")}
                className={tabCls(isCustom)}
              >
                Custom rules
              </button>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label>Risk method</Label>
              <select
                data-testid="detail-risk-method-select"
                className={inputCls}
                disabled={!isCustom}
                value={rules.risk_method}
                onChange={set("risk_method")}
              >
                <option value="fixed">Fixed dollar per trade</option>
                <option value="percent">Percent of balance</option>
              </select>
              <p className="text-[10px] font-mono text-zinc-600 mt-1">
                template: {tpl.risk_method}
              </p>
            </div>

            {rules.risk_method === "fixed" ? (
              <div>
                <Label>Risk per trade ($)</Label>
                <input
                  data-testid="detail-risk-fixed-input"
                  type="number"
                  step="1"
                  min="1"
                  className={inputCls}
                  disabled={!isCustom}
                  value={rules.risk_fixed_amount}
                  onChange={set("risk_fixed_amount")}
                />
                <p className="text-[10px] font-mono text-zinc-600 mt-1">
                  template: ${tpl.risk_fixed_amount}
                </p>
              </div>
            ) : (
              <div>
                <Label>Risk per trade (% of balance)</Label>
                <input
                  data-testid="detail-risk-percent-input"
                  type="number"
                  step="0.1"
                  min="0.1"
                  className={inputCls}
                  disabled={!isCustom}
                  value={rules.risk_percent}
                  onChange={set("risk_percent")}
                />
                <p className="text-[10px] font-mono text-zinc-600 mt-1">
                  template: {tpl.risk_percent}%
                </p>
              </div>
            )}

            <div>
              <Label>Daily loss limit ($)</Label>
              <input
                data-testid="detail-daily-loss-limit-input"
                type="number"
                step="1"
                min="1"
                className={inputCls}
                disabled={!isCustom}
                value={rules.daily_loss_limit}
                onChange={set("daily_loss_limit")}
              />
              <p className="text-[10px] font-mono text-zinc-600 mt-1">
                template: ${tpl.daily_loss_limit}
              </p>
            </div>
            <div>
              <Label>Loss limit mode</Label>
              <select
                data-testid="detail-loss-limit-mode-select"
                className={inputCls}
                disabled={!isCustom}
                value={rules.loss_limit_mode}
                onChange={set("loss_limit_mode")}
              >
                <option value="fixed">Fixed — profit does not extend it</option>
                <option value="trailing">Grows with booked profit</option>
              </select>
              <p className="text-[10px] font-mono text-zinc-600 mt-1">
                template: {tpl.loss_limit_mode}
              </p>
            </div>
            <div>
              <Label>Max open trades</Label>
              <input
                data-testid="detail-max-open-trades-input"
                type="number"
                step="1"
                min="1"
                className={inputCls}
                disabled={!isCustom}
                value={rules.max_open_trades}
                onChange={set("max_open_trades")}
              />
              <p className="text-[10px] font-mono text-zinc-600 mt-1">
                template: {tpl.max_open_trades}
              </p>
            </div>
            <div>
              <Label>Max trades per day</Label>
              <input
                data-testid="detail-max-trades-per-day-input"
                type="number"
                step="1"
                min="0"
                className={inputCls}
                disabled={!isCustom}
                value={rules.max_trades_per_day ?? 0}
                onChange={set("max_trades_per_day")}
              />
              <p className="text-[10px] font-mono text-zinc-600 mt-1">
                template: {tpl.max_trades_per_day || "unlimited"} · 0 = unlimited
              </p>
            </div>
            <div>
              <Label>Daily profit target ($)</Label>
              <input
                data-testid="detail-daily-profit-target-input"
                type="number"
                step="1"
                min="0"
                className={inputCls}
                disabled={!isCustom}
                value={rules.daily_profit_target ?? 0}
                onChange={set("daily_profit_target")}
              />
              <p className="text-[10px] font-mono text-zinc-600 mt-1">
                template: {tpl.daily_profit_target ? `$${tpl.daily_profit_target}` : "off"} · 0 = off
              </p>
            </div>
            <div>
              <Label>Window start (IST)</Label>
              <input
                data-testid="detail-window-start-input"
                type="time"
                className={inputCls}
                disabled={!isCustom}
                value={rules.window_start}
                onChange={set("window_start")}
              />
              <p className="text-[10px] font-mono text-zinc-600 mt-1">
                template: {tpl.window_start}
              </p>
            </div>
            <div>
              <Label>Window end (IST)</Label>
              <input
                data-testid="detail-window-end-input"
                type="time"
                className={inputCls}
                disabled={!isCustom}
                value={rules.window_end}
                onChange={set("window_end")}
              />
              <p className="text-[10px] font-mono text-zinc-600 mt-1">
                template: {tpl.window_end}
              </p>
            </div>
            <div>
              <Label>TP1 close ratio (%)</Label>
              <input
                data-testid="detail-tp1-ratio-input"
                type="number"
                step="1"
                min="0"
                max="99"
                className={inputCls}
                disabled={!isCustom}
                value={rules.tp1_ratio}
                onChange={set("tp1_ratio")}
              />
              <p className="text-[10px] font-mono text-zinc-600 mt-1">
                template: {tpl.tp1_ratio}%
              </p>
            </div>
            <div>
              <Label>Move SL to break-even at TP1</Label>
              <select
                data-testid="detail-move-be-select"
                className={inputCls}
                disabled={!isCustom}
                value={rules.move_be_on_tp1 === false ? "off" : "on"}
                onChange={(e) =>
                  setRules((r) => ({ ...r, move_be_on_tp1: e.target.value === "on" }))
                }
              >
                <option value="on">On — stop goes to entry</option>
                <option value="off">Off — stop stays put</option>
              </select>
              <p className="text-[10px] font-mono text-zinc-600 mt-1">
                template: {tpl.move_be_on_tp1 === false ? "off" : "on"}
              </p>
            </div>
            <div>
              <Label>Default TP1 (R multiple)</Label>
              <input
                data-testid="detail-tp1-r-input"
                type="number"
                step="0.1"
                min="0"
                className={inputCls}
                disabled={!isCustom}
                value={rules.tp1_r ?? 0}
                onChange={set("tp1_r")}
              />
              <p className="text-[10px] font-mono text-zinc-600 mt-1">
                template: {tpl.tp1_r}R
              </p>
            </div>
            <div>
              <Label>Default TP2 (R multiple)</Label>
              <input
                data-testid="detail-tp2-r-input"
                type="number"
                step="0.1"
                min="0"
                className={inputCls}
                disabled={!isCustom}
                value={rules.tp2_r ?? 0}
                onChange={set("tp2_r")}
              />
              <p className="text-[10px] font-mono text-zinc-600 mt-1">
                template: {tpl.tp2_r}R
              </p>
            </div>
            <div>
              <Label>Timezone for the window</Label>
              <input
                data-testid="detail-window-tz-input"
                className={inputCls}
                disabled={!isCustom}
                value={rules.window_tz ?? "Asia/Kolkata"}
                onChange={set("window_tz")}
                placeholder="Asia/Kolkata"
              />
              <p className="text-[10px] font-mono text-zinc-600 mt-1">
                template: {tpl.window_tz || "Asia/Kolkata"} · counters stay IST
              </p>
            </div>
            <div>
              <Label>New SL allowance (%)</Label>
              <input
                data-testid="detail-sl-allowance-input"
                type="number"
                step="1"
                min="0"
                max="200"
                className={inputCls}
                disabled={!isCustom}
                value={rules.sl_allowance_pct ?? 25}
                onChange={set("sl_allowance_pct")}
              />
              <p className="text-[10px] font-mono text-zinc-600 mt-1">
                template: {tpl.sl_allowance_pct ?? 25}% of entry→First SL
              </p>
            </div>
            <div>
              <Label>Trailing SL distance (%)</Label>
              <input
                data-testid="detail-trail-pct-input"
                type="number"
                step="1"
                min="1"
                max="200"
                className={inputCls}
                disabled={!isCustom}
                value={rules.trail_pct ?? 25}
                onChange={set("trail_pct")}
              />
              <p className="text-[10px] font-mono text-zinc-600 mt-1">
                template: {tpl.trail_pct ?? 25}% · gap below the highest price reached
              </p>
            </div>
            <div>
              <Label>External trade detection</Label>
              <select
                data-testid="detail-external-action-select"
                className={inputCls}
                disabled={!isCustom}
                value={rules.external_action ?? "alert"}
                onChange={set("external_action")}
              >
                <option value="alert">Alert only</option>
                <option value="close">Auto-close</option>
              </select>
              <p className="text-[10px] font-mono text-zinc-600 mt-1">
                template: {tpl.external_action === "close" ? "auto-close" : "alert only"}
              </p>
            </div>
            <div className="sm:col-span-2 lg:col-span-3 rounded-sm border border-blue-900/60 bg-blue-500/[0.04] p-3">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <Label>This trader's instruments</Label>
                <span className="px-1.5 py-0.5 rounded-sm bg-blue-500/15 text-blue-300 text-[9px] uppercase font-bold tracking-wide">
                  Level 2 of 2 · subset
                </span>
              </div>
              <p className="text-[11px] font-mono text-zinc-400 mb-2 leading-relaxed"
                 data-testid="trader-list-explainer">
                Picked <span className="text-blue-300">from</span> the desk-wide master list
                {(tpl.allowed_instruments || []).length
                  ? ` (${pickable.length} symbols)`
                  : " (not set yet, so the whole broker catalog is offered)"}
                . Narrowing it here only restricts this trader — it can never add an instrument
                the master list leaves out.
              </p>
              <InstrumentChecklist
                testid="detail-allowed-instruments"
                disabled={!isCustom}
                catalog={pickable}
                value={rules.allowed_instruments || []}
                onChange={(v) => setRules((r) => ({ ...r, allowed_instruments: v }))}
                emptyHint={
                  (tpl.allowed_instruments || []).length
                    ? "The global list is empty for this trader."
                    : "No broker catalog yet — connect an EA and it will report its symbols."
                }
              />
              <p className="text-[10px] font-mono text-zinc-600 mt-1">
                {(tpl.allowed_instruments || []).length
                  ? `Picked from the ${pickable.length} instruments on the master list`
                  : `Picked from the ${pickable.length} symbols in the broker catalog`}{" "}
                · nothing selected = the whole master list applies · any other instrument triggers
                the external trade rule
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              data-testid="detail-save-rules-btn"              onClick={save}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2.5 rounded-sm text-xs font-semibold uppercase tracking-[0.14em] transition-colors duration-200 active:scale-95 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save rules"}
            </button>
            <span
              data-testid="rules-mode-badge"
              className={`px-2 py-1 rounded-sm text-[10px] uppercase font-semibold ${
                detail.has_override
                  ? "bg-blue-500/10 text-blue-400"
                  : "bg-zinc-800 text-zinc-400"
              }`}
            >
              currently: {detail.has_override ? "custom" : "template"}
            </span>
          </div>
        </div>
        </CollapsibleCard>

        <CollapsibleCard
          title="Alerts"
          count={alerts.length}
          storageKey="detail-alerts"
          testid="detail-alerts-panel"
        >
          <div className="max-h-64 overflow-y-auto divide-y divide-zinc-900">
            {alerts.length === 0 && (
              <p className="px-4 py-6 text-center text-xs text-zinc-500">
                No alerts for this trader.
              </p>
            )}
            {alerts.map((a) => (
              <div key={a.alert_id} className="px-4 py-2.5 flex items-start gap-3">
                <span className="text-[10px] uppercase tracking-[0.1em] font-bold text-amber-400 shrink-0 w-40">
                  {a.type}
                </span>
                <span className="font-mono text-[11px] text-zinc-500 shrink-0 w-20">
                  {a.symbol || "—"}
                </span>
                <span className="text-xs text-zinc-300 flex-1">{a.message}</span>
                <span className="font-mono text-[10px] text-zinc-600 shrink-0">
                  {a.created_at_ist}
                </span>
              </div>
            ))}
          </div>
        </CollapsibleCard>


        <div className="flex flex-wrap items-center gap-2">
          {["week", "month", "all"].map((x) => (
            <button
              key={x}
              data-testid={`detail-period-${x}-btn`}
              onClick={() => {
                setPeriod(x);
                setPage(1);
              }}
              className={tabCls(period === x)}
            >
              {x}
            </button>
          ))}
          <span className="text-xs text-zinc-600 font-mono ml-2">
            {history ? `${history.total} fills · net ${money(history.realised)}` : "—"}
          </span>
        </div>

        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-4">
          <CollapsibleCard title="Day by day" storageKey="detail-daywise" testid="trader-daywise">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="bg-black/40">
                  <tr>
                    {["Date", "Fills", "P&L"].map((h) => (
                      <th key={h} className={th}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(days?.days?.length ?? 0) === 0 && (
                    <tr>
                      <td colSpan={3} className="px-4 py-8 text-center text-xs text-zinc-600" data-testid="no-daywise">
                        No closed trades in this period.
                      </td>
                    </tr>
                  )}
                  {[...(days?.days ?? [])].reverse().map((d) => (
                    <tr
                      key={d.day}
                      data-testid={`daywise-row-${d.day}`}
                      className="border-b border-zinc-800"
                    >
                      <td className={td}>{d.day}</td>
                      <td className={td}>{d.trades}</td>
                      <td
                        className={`${td} ${
                          d.profit > 0 ? "text-emerald-400" : d.profit < 0 ? "text-red-400" : ""
                        }`}
                      >
                        {money(d.profit)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {days && (
              <p className="px-4 py-3 text-[11px] font-mono text-zinc-600 border-t border-zinc-800">
                {days.green_days} green · {days.red_days} red · net {money(days.total)}
              </p>
            )}
          </CollapsibleCard>
          </div>

          <div className="col-span-12 lg:col-span-8">
          <CollapsibleCard title="All trades" count={history?.total} storageKey="detail-trades"
                           testid="trader-history">
            <div className="md:hidden divide-y divide-zinc-800">
              {(history?.items?.length ?? 0) === 0 && (
                <p className="px-4 py-10 text-center text-xs text-zinc-600"
                   data-testid="no-trader-history-mobile">
                  No closed trades in this period.
                </p>
              )}
              {history?.items?.map((d) => (
                <MobileRow
                  key={d.deal_id}
                  testid={`trader-history-card-${d.deal_id}`}
                  title={
                    <>
                      <span className="font-mono text-xs font-semibold text-zinc-100">
                        {d.symbol}
                      </span>
                      <span className="block font-mono text-[10px] text-zinc-500">
                        {d.closed_at_ist || d.day}
                      </span>
                    </>
                  }
                  badge={
                    <span
                      className={`px-1.5 py-0.5 rounded-sm text-[9px] uppercase font-bold shrink-0 ${
                        d.side === "buy"
                          ? "bg-emerald-500/10 text-emerald-400"
                          : d.side === "sell"
                          ? "bg-red-500/10 text-red-400"
                          : "bg-zinc-800 text-zinc-400"
                      }`}
                    >
                      {d.side || "—"}
                    </span>
                  }
                  trailing={
                    <span
                      className={`font-mono text-xs font-bold shrink-0 ${
                        d.profit > 0 ? "text-emerald-400"
                        : d.profit < 0 ? "text-red-400" : "text-zinc-200"
                      }`}
                    >
                      {money(d.profit)}
                    </span>
                  }
                  rows={[
                    ["Size", d.volume],
                    ["Reason", (d.kind || "—").toUpperCase()],
                    ["Entry", d.entry || "—"],
                    ["Exit", d.exit_price || "—"],
                    ["Source", d.source || "Strict RR"],
                  ]}
                />
              ))}
            </div>

            <div className="overflow-x-auto hidden md:block">
              <table className="w-full border-collapse min-w-[760px]">
                <thead className="bg-black/40">
                  <tr>
                    {["Closed (IST)", "Symbol", "Side", "Size", "Entry", "Exit", "Reason", "Source", "P&L"].map(
                      (h) => (
                        <th key={h} className={th}>
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {(history?.items?.length ?? 0) === 0 && (
                    <tr>
                      <td colSpan={9} className="px-4 py-10 text-center text-xs text-zinc-600" data-testid="no-trader-history">
                        No closed trades in this period.
                      </td>
                    </tr>
                  )}
                  {history?.items?.map((d) => (
                    <tr
                      key={d.deal_id}
                      data-testid={`trader-history-row-${d.deal_id}`}
                      className="border-b border-zinc-800 hover:bg-zinc-800/40 transition-colors duration-200"
                    >
                      <td className={`${td} text-zinc-400`}>{d.closed_at_ist || d.day}</td>
                      <td className={`${td} text-zinc-100 font-semibold`}>{d.symbol}</td>
                      <td className={td}>{d.side || "—"}</td>
                      <td className={td}>{d.volume}</td>
                      <td className={td}>{d.entry || "—"}</td>
                      <td className={td}>{d.exit_price || "—"}</td>
                      <td className={`${td} text-zinc-400 uppercase text-[10px]`}>{d.kind}</td>
                      <td className={td}>{d.source || "Strict RR"}</td>
                      <td
                        className={`${td} ${
                          d.profit > 0 ? "text-emerald-400" : d.profit < 0 ? "text-red-400" : ""
                        }`}
                      >
                        {money(d.profit)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-800">
              <span className="text-xs text-zinc-500 font-mono" data-testid="detail-pagination-info">
                Page {history?.page ?? 1} of {history?.pages ?? 1}
              </span>
              <div className="flex gap-2">
                <button
                  data-testid="detail-prev-page-btn"
                  disabled={(history?.page ?? 1) <= 1}
                  onClick={() => setPage((x) => Math.max(x - 1, 1))}
                  className="flex items-center gap-1 border border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white px-3 py-1.5 rounded-sm text-xs uppercase font-semibold transition-colors duration-200 disabled:opacity-40"
                >
                  <ChevronLeft className="h-3.5 w-3.5" /> Prev
                </button>
                <button
                  data-testid="detail-next-page-btn"
                  disabled={(history?.page ?? 1) >= (history?.pages ?? 1)}
                  onClick={() => setPage((x) => x + 1)}
                  className="flex items-center gap-1 border border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white px-3 py-1.5 rounded-sm text-xs uppercase font-semibold transition-colors duration-200 disabled:opacity-40"
                >
                  Next <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </CollapsibleCard>
          </div>
        </div>
      </div>
    </Shell>
  );
}
